import { formatMoneyFlexible } from "./analysisFormat";

// Each widget stacks (or, for the invoice currency widget, groups) at most
// this many dimension-value series per date bucket, folding the rest into a
// single "Others" series — otherwise a dimension like country renders dozens
// of razor-thin stack segments.
export const WIDGET_MAX_SERIES = 10;

// Fixed hue order, never cycled/reassigned per filter (color follows the
// entity, not its rank — a filter that changes which values are present
// must not repaint the survivors). The first 8 are the app's validated
// adjacent-pair CVD-safe sequence for stacked bars; slots 9-10 (only ever
// reached by high-cardinality dimensions like country) extend it with two
// more visually distinct hues that haven't been run through the same
// validator. "Others" always gets a neutral gray so it reads as an
// aggregate, not a real value.
const SERIES_COLORS = [
  "#2a78d6", // blue
  "#eb6834", // orange
  "#1baf7a", // aqua
  "#eda100", // yellow
  "#e87ba4", // magenta
  "#008300", // green
  "#4a3aa7", // violet
  "#e34948", // red
  "#8c564b", // brown
  "#bcbd22", // olive
];
const OTHERS_COLOR = "#adb5bd";

// Purchases/renewals/wallet-transactions/purchase-invoice rows are
// (date bucket, breakdown value) pairs — pivot into one ApexCharts series
// per breakdown value, with the date buckets as categories. This is the
// same shape fetchUsersAnalysisChart already returns directly (it does the
// x/y pivot server-side), so Users-tab widgets skip this and go straight to
// capSeriesWithOthers.
export const pivotByDateAndBreakdown = (rows, valueKey) => {
  const categories = [];
  const seriesNames = [];
  const valuesByNameThenCategory = {};

  rows.forEach((row) => {
    const x = row.group;
    const y = row.breakdown ?? "unknown";
    if (!categories.includes(x)) categories.push(x);
    if (!seriesNames.includes(y)) seriesNames.push(y);
    (valuesByNameThenCategory[y] ??= {})[x] = Number(row[valueKey] || 0);
  });

  const series = seriesNames.map((name) => ({
    name,
    data: categories.map((x) => valuesByNameThenCategory[name]?.[x] ?? 0),
  }));
  return { categories, series };
};

// Caps a {categories, series} chart to the top WIDGET_MAX_SERIES series by
// total magnitude (summed across every date bucket), folding the rest into
// a single "Others" series (summed per bucket, not just once) — kept sorted
// descending so the largest values stay individually visible.
export const capSeriesWithOthers = (chartData, n = WIDGET_MAX_SERIES) => {
  const categories = chartData.categories ?? [];
  const series = chartData.series ?? [];

  const withTotals = series.map((s) => ({
    ...s,
    total: (s.data || []).reduce((sum, v) => sum + (Number(v) || 0), 0),
  }));
  withTotals.sort((a, b) => b.total - a.total);

  if (withTotals.length <= n) return { categories, series: withTotals };

  const kept = withTotals.slice(0, n);
  const rest = withTotals.slice(n);
  const others = {
    name: "Others",
    data: categories.map((_, i) => rest.reduce((sum, s) => sum + (s.data[i] || 0), 0)),
  };
  return { categories, series: [...kept, others] };
};

// Bar chart: X = date bucket (per the selected granularity), one series per
// dimension value — stacked by default (Users/Purchases/Wallet/most invoice
// widgets), or grouped side-by-side when `stacked: false` (the invoice
// Currency widget, where stacking would sum different currencies' amounts
// together into a meaningless bar height).
export const buildStackedBarChartOptions = (categories, series, { isMoney = false, stacked = true } = {}) => {
  const formatValue = isMoney
    ? formatMoneyFlexible
    : (val) => Math.round(val).toLocaleString();
  const colors = series.map((s, i) => (s.name === "Others" ? OTHERS_COLOR : SERIES_COLORS[i % SERIES_COLORS.length]));

  return {
    chart: {
      stacked,
      toolbar: { show: false },
      animations: { enabled: false },
      fontFamily: "inherit",
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: stacked ? "last" : undefined,
        columnWidth: stacked ? "55%" : "70%",
      },
    },
    colors,
    legend: {
      show: series.length > 1,
      position: "bottom",
      fontSize: "12px",
      markers: { size: 6 },
    },
    dataLabels: { enabled: false },
    grid: {
      borderColor: "#e9ecef",
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      categories,
      labels: { style: { colors: "#8898aa", fontSize: "11px" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      // vue3-apexcharts JSON-serializes reactive :options updates (which
      // happens as soon as a widget's async data replaces its initial empty
      // state), silently dropping function values like labels.formatter —
      // decimalsInFloat is a plain number, so it survives and keeps ticks
      // clean even once the formatter below is gone.
      decimalsInFloat: 0,
      labels: { style: { colors: "#8898aa" }, formatter: (val) => formatValue(val) },
    },
    tooltip: {
      shared: true,
      // ApexCharts' bar-chart default has intersect:true, which is mutually
      // exclusive with shared:true — override explicitly rather than rely
      // on the default.
      intersect: false,
      // Append the bucket's total to the tooltip header, next to the date
      // label — only when stacked, since grouped (unstacked) charts are only
      // used where series shouldn't be summed (e.g. mixed currencies), so a
      // total there would be meaningless. Omit the `x` key entirely rather
      // than setting it to undefined — ApexCharts' option merge treats a
      // present-but-undefined key as an override of its own tooltip.x
      // default, wiping it out and crashing when it later reads `.show` off
      // the resulting undefined.
      ...(stacked
        ? {
            x: {
              formatter: (val, { dataPointIndex, w }) => {
                const bucketTotal = w.globals.series.reduce(
                  (sum, s) => sum + (Number(s[dataPointIndex]) || 0),
                  0
                );
                return `${val} (Total: ${formatValue(bucketTotal)})`;
              },
            },
          }
        : {}),
      y: { formatter: (val) => formatValue(val) },
    },
  };
};
